
import { AlertTitle } from '@mui/material';
import { asyncDebounce, IFormValidationError, InstitutionsContext, IValidationError, OnlinePatientManagementContext, useAuthenticatedUser, useSnackbar, ValidationErrorType } from '@ngt/opms';
import { useCallback, useContext, useMemo } from 'react';

import { IFormState, IFormValidate } from '@ngt/forms-core';
import pluralize from 'pluralize';
import { useNavigate } from 'react-router-dom';
import { FormDefinitionType, LookupYesNoType, Patient, SaConsentStatus, ServicesAustraliaConsent, ServicesAustraliaConsentPostSaveWithCodes, SourceDocument } from '../../api/dtos';
import useGetServicesAustraliaConsent from '../data/useGetServicesAustraliaConsent';
import { DateTime } from 'luxon';


export interface IUseSaConsentFormOptions {
    patient?: Patient;
}

const SA_CONSENT_FILE_TYPE = 3;

const useSaConsentForm = <TForm extends ServicesAustraliaConsent>({
    patient
}: IUseSaConsentFormOptions) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { data: user } = useAuthenticatedUser();

    const onlinePatientManagement = useContext(OnlinePatientManagementContext);
    const { data: institutions } = useContext(InstitutionsContext);

    const institution = useMemo(() => {
        return institutions?.find(i => i.id === patient?.institutionId);
    }, [institutions, patient]);

    const {
        data: consentForm,
        loading,
        validating
    } = useGetServicesAustraliaConsent(patient);

    const formInitial = useMemo(() => {
        let sourceDocuments = [] as SourceDocument[];

        switch (consentForm?.saConsentStatus) {
            case SaConsentStatus.Pending:
                let consentDocument = sourceDocuments.find(x => x.fileTypeId === SA_CONSENT_FILE_TYPE)

                if (!consentDocument) {
                    consentDocument = {
                        parentFormId: consentForm?.id,
                        parentFormDefinitionId: FormDefinitionType.ServicesAustraliaConsent,
                        repeat: 1,
                        
                        fileTypeId: SA_CONSENT_FILE_TYPE,
                        
                        enteredBy: user?.displayName,
                        enteredDate: undefined
                    } as SourceDocument
                }

                sourceDocuments.push(consentDocument);
                
                break;

            case SaConsentStatus.PendingReview:
            case SaConsentStatus.Complete:
            case SaConsentStatus.Processed:
                sourceDocuments = consentForm.sourceDocuments ?? [];
                break;
            
            default: 
                sourceDocuments = [];
                break;
        }

        return {
            ...consentForm,
            reviewedBy:
                !!consentForm?.reviewedBy && 
                consentForm?.saConsentStatus !== SaConsentStatus.PendingReview ? 
                    consentForm.reviewedBy : 
                    user?.displayName,
            reviewedDate:
                !!consentForm?.reviewedDate && 
                consentForm?.saConsentStatus !== SaConsentStatus.PendingReview ? 
                    DateTime.fromISO(consentForm.reviewedDate) : 
                    DateTime.local().toISO({ includeOffset: false }),
            sourceDocuments: sourceDocuments
        } as ServicesAustraliaConsent;
    }, [consentForm, user]);


    const allowSubmit = useCallback(async ({ errors }: IFormState<TForm, IValidationError>): Promise<boolean> => {
        if (!errors) {
            return true;
        }

        return !Object
            .keys(errors)
            .some(key => 
                errors[key] && 
                errors[key].some(e => 
                    e.type != null && 
                    e.type >= ValidationErrorType.Normal
                )
            );
    }, []);

    const onValidate: IFormValidate<ServicesAustraliaConsent, IFormValidationError> = useCallback(async (formState) => {
        let errors: Record<string, IFormValidationError[]> = {};

        // If submit button clicked, skip validation and return errors list
        if (formState.submitting == true &&
            (formState.value as any)?.saveAndReturn === true
        ) return errors;

        if (formState.value?.saConsentStatus === SaConsentStatus.PendingReview) {
            const idx = formState.value?.sourceDocuments?.findIndex(x => x.fileTypeId === 3);
            const saConsentDocument = formState.value?.sourceDocuments?.[idx];

            if (saConsentDocument?.name === undefined ||
                saConsentDocument?.name === null) {
                errors[`sourceDocuments[${idx}].name`] = [
                    {
                        property: `sourceDocuments[${idx}].name`,
                        code: 'SAC-001',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (saConsentDocument?.file === undefined ||
                saConsentDocument?.file === null) {
                errors[`sourceDocuments[${idx}].file`] = [
                    {
                        property: `sourceDocuments[${idx}].file`,
                        code: 'SAC-002',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

        } else if (formState.value?.saConsentStatus === SaConsentStatus.Pending) {
            if (formState.value?.consentFormVersion === undefined ||
                formState.value?.consentFormVersion === null) {
                errors['consentFormVersion'] = [
                    {
                        property: 'consentFormVersion',
                        code: 'SAC-003',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.consentFormDate === undefined ||
                formState.value?.consentFormDate === null) {
                errors['consentFormDate'] = [
                    {
                        property: 'consentFormDate',
                        code: 'SAC-004',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.currentTmdConsentFormVersion === undefined ||
                formState.value?.currentTmdConsentFormVersion === null) {
                errors['currentTmdConsentFormVersion'] = [
                    {
                        property: 'currentTmdConsentFormVersion',
                        code: 'SAC-005',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.participantSignedCorrectVersion === undefined ||
                formState.value?.participantSignedCorrectVersion === null) {
                errors['participantSignedCorrectVersion'] = [
                    {
                        property: 'participantSignedCorrectVersion',
                        code: 'SAC-007',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.consentFullyCompleted === undefined ||
                formState.value?.consentFullyCompleted === null) {
                errors['consentFullyCompleted'] = [
                    {
                        property: 'consentFullyCompleted',
                        code: 'SAC-009',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.correctionsSigned === undefined ||
                formState.value?.correctionsSigned === null) {
                errors['correctionsSigned'] = [
                    {
                        property: 'correctionsSigned',
                        code: 'SAC-011',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.witnessIdentityConfirmed === undefined ||
                formState.value?.witnessIdentityConfirmed === null) {
                errors['witnessIdentityConfirmed'] = [
                    {
                        property: 'witnessIdentityConfirmed',
                        code: 'SAC-013',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

        } else if (formState.value?.saConsentStatus === SaConsentStatus.Complete) {
            if (formState.value?.consentFormVersion === undefined ||
                formState.value?.consentFormVersion === null) {
                errors['consentFormVersion'] = [
                    {
                        property: 'consentFormVersion',
                        code: 'SAC-003',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.consentFormDate === undefined ||
                formState.value?.consentFormDate === null) {
                errors['consentFormDate'] = [
                    {
                        property: 'consentFormDate',
                        code: 'SAC-004',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.currentTmdConsentFormVersion === undefined ||
                formState.value?.currentTmdConsentFormVersion === null) {
                errors['currentTmdConsentFormVersion'] = [
                    {
                        property: 'currentTmdConsentFormVersion',
                        code: 'SAC-005',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.currentTmdConsentFormVersion !== LookupYesNoType.Yes) {
                errors['currentTmdConsentFormVersion'] = [
                    {
                        property: 'currentTmdConsentFormVersion',
                        code: 'SAC-006',
                        message: 'Yes is required for this field to be submitted.',
                        detailedMessage: 'Yes is required for this field to be submitted.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.participantSignedCorrectVersion === undefined ||
                formState.value?.participantSignedCorrectVersion === null) {
                errors['participantSignedCorrectVersion'] = [
                    {
                        property: 'participantSignedCorrectVersion',
                        code: 'SAC-007',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.participantSignedCorrectVersion !== LookupYesNoType.Yes) {
                errors['participantSignedCorrectVersion'] = [
                    {
                        property: 'participantSignedCorrectVersion',
                        code: 'SAC-008',
                        message: 'Yes is required for this field to be submitted.',
                        detailedMessage: 'Yes is required for this field to be submitted.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.consentFullyCompleted === undefined ||
                formState.value?.consentFullyCompleted === null) {
                errors['consentFullyCompleted'] = [
                    {
                        property: 'consentFullyCompleted',
                        code: 'SAC-009',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.consentFullyCompleted !== LookupYesNoType.Yes) {
                errors['consentFullyCompleted'] = [
                    {
                        property: 'consentFullyCompleted',
                        code: 'SAC-010',
                        message: 'Yes is required for this field to be submitted.',
                        detailedMessage: 'Yes is required for this field to be submitted.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.correctionsSigned === undefined ||
                formState.value?.correctionsSigned === null) {
                errors['correctionsSigned'] = [
                    {
                        property: 'correctionsSigned',
                        code: 'SAC-011',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.correctionsSigned === LookupYesNoType.No) {
                errors['correctionsSigned'] = [
                    {
                        property: 'correctionsSigned',
                        code: 'SAC-012',
                        message: 'Yes is required for this field to be submitted.',
                        detailedMessage: 'Yes is required for this field to be submitted.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }

            if (formState.value?.witnessIdentityConfirmed === undefined ||
                formState.value?.witnessIdentityConfirmed === null) {
                errors['witnessIdentityConfirmed'] = [
                    {
                        property: 'witnessIdentityConfirmed',
                        code: 'SAC-013',
                        message: 'This field is required.',
                        detailedMessage: 'This field is required.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
            if (formState.value?.witnessIdentityConfirmed === LookupYesNoType.No) {
                errors['witnessIdentityConfirmed'] = [
                    {
                        property: 'witnessIdentityConfirmed',
                        code: 'SAC-014',
                        message: 'Yes is required for this field to be submitted.',
                        detailedMessage: 'Yes is required for this field to be submitted.',
                        type: ValidationErrorType.Normal
                    }
                ];
            }
        }

        return errors;
    }, []);

    // debounce validation functions to reduce calls to the server and associate lag.
    const debouncedValidate = useMemo(() => {
        return asyncDebounce(onValidate, 500);
    }, [onValidate]);


    const onFormSubmitValidationFailure = useCallback(async ({ errors }: IFormState<TForm, IValidationError>, validationError: boolean) => {
        if (validationError) {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Error
                    </AlertTitle>
                    An error occurred while attempting to save the SA consent form.
                </>,
                { variant: 'critical' }
            );

        } else {
            const criticalErrors = Object
                .keys(errors)
                .reduce((array: IValidationError[], key: string) => {
                    const propertyErrors = errors[key]?.reduce((propertyArray: IValidationError[], e: IValidationError) => {
                        if (e.type != null && e.type >= ValidationErrorType.Normal) {
                            return [...propertyArray, e]
                        }

                        return propertyArray;
                    }, [])

                    return [...array, ...propertyErrors]
                }, []);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        SA Consent Not Saved
                    </AlertTitle>
                    Please correct the {criticalErrors.length} blocking {pluralize('error', criticalErrors.length)} and try again.
                </>,
                { variant: 'critical' }
            );
        }
    }, [enqueueSnackbar]);


    const onFormSubmitFailure = useCallback(async ({ }: IFormState<TForm, IValidationError>) => {
        enqueueSnackbar(
            <>
                <AlertTitle>
                    Error
                </AlertTitle>
                An error occurred while attempting to save the SA consent form.
            </>,
            { variant: 'critical' }
        );
    }, [enqueueSnackbar]);

    const handleReject = useCallback(async (form: ServicesAustraliaConsent) => {
        let copy = { ...form };
        copy.saConsentStatus = SaConsentStatus.Pending;
        copy.sourceDocuments = form.sourceDocuments?.filter(x => x.fileTypeId !== 3)
        copy.isHistoryEntry = true

        try {
            await onlinePatientManagement
                .serviceStackClient
                .post(new ServicesAustraliaConsentPostSaveWithCodes({
                    form: copy ?? undefined,
                    institutionCode: institution?.code, 
                    patientStudyNumber: patient?.studyNumber, 
                    eventDefinitionCode: "consent", 
                    eventRepeat: 1, 
                    formRepeat: 1, 
                    createPatient: false,
                    createEvent: false
                }));

            navigate("/");

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        SA Consent Form Rejected
                    </AlertTitle>
                    The Service Australia consent form was rejected successfully
                </>,
                { variant: 'success' }
            );

        } catch (error) {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Error
                    </AlertTitle>
                    An error occurred while attempting to save the Service Australia consent form.
                </>,
                { variant: 'critical' }
            );
        }

        
    }, [institution?.code, patient?.studyNumber]);


    const handleSubmit = useCallback(async ({ value }: IFormState<TForm, IValidationError>) => {
        try {
            let copy = value ? { ...value } : undefined;

            await onlinePatientManagement
                .serviceStackClient
                .post(new ServicesAustraliaConsentPostSaveWithCodes({
                    form: copy,
                    institutionCode: institution?.code, 
                    patientStudyNumber: patient?.studyNumber, 
                    eventDefinitionCode: "consent", 
                    eventRepeat: 1, 
                    formRepeat: 1, 
                    createPatient: false,
                    createEvent: false
                }));

            navigate('/');

            if (copy?.saConsentStatus === SaConsentStatus.Complete) {
                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            SA Consent Form Completed
                        </AlertTitle>
                        The Service Australia consent form was completed successfully.
                    </>,
                    { variant: 'success' }
                );    
            } else {
                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            SA Consent Form Saved
                        </AlertTitle>
                        The Service Australia consent form was saved successfully.
                    </>,
                    { variant: 'success' }
                );
            }

        } catch (error) {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Error
                    </AlertTitle>
                    An error occurred while attempting to save the Service Australia consent form.
                </>,
                { variant: 'critical' }
            );
        }
    }, [enqueueSnackbar, history, institution, onlinePatientManagement, patient]);

    return {
        formInitial,
        loading,
        validating,
        handleSubmit,
        handleReject,
        onFormSubmitFailure,
        onFormSubmitValidationFailure,
        validate: debouncedValidate,
        allowSubmit
    }
}

export default useSaConsentForm;